import { EnumUserRoles } from 'entities/Users/model/types'
import { type NavArea } from 'entities/WebMcp/model/navAreas'
import { roleAtLeast } from 'entities/WebMcp/model/roleGating'
import { DISCOVERY_ITEMS } from 'entities/WebMcp/model/discoveryItems'
import { ROUTE_CAPABILITIES } from 'entities/WebMcp/model/routeCapabilities'
import { remainingOnboardingPills } from 'entities/WebMcp/model/onboardingSteps'
import { type OnboardingStatus } from 'entities/WebMcp/lib/onboardingSignals'

export type StarterScope =
  | { kind: 'global' }
  | { kind: 'onboarding' }
  | { kind: 'area'; area: NavArea }

/** One thing the user can start, in the shape the empty state and the command
 *  menu both read, so a label or prompt change reaches both. */
export interface AgentStarter {
  key: string
  label: string
  prompt: string
  minRole: EnumUserRoles
  scope: StarterScope
}

/** A starter whose scope is fixed by where it is declared. */
export type ScopedStarter = Omit<AgentStarter, 'scope'>

// Global and role-free: the tour depends on the person, not on the account or the
// page, so nothing filters it out (UC-1).
const TOUR: AgentStarter = {
  key: 'tour',
  label: 'Welcome tour',
  prompt: 'Give me a tour of Salesmsg',
  minRole: EnumUserRoles.VIEW_ONLY,
  scope: { kind: 'global' },
}

export const TOUR_STARTER = TOUR

// Prefixed by source because the same job appears in two lists: "Find a
// conversation" is both a quick action and an Inboxes page suggestion.
export const globalStarters = (role: EnumUserRoles): AgentStarter[] =>
  [TOUR].filter((s) => roleAtLeast(role, s.minRole)).map((s) => ({ ...s, key: `global:${s.key}` }))

/** Confirmed-incomplete, role-permitted onboarding steps, in checklist order. */
export const onboardingStarters = (
  onboarding: OnboardingStatus,
  role: EnumUserRoles
): AgentStarter[] =>
  remainingOnboardingPills(onboarding, role).map((p) => ({
    key: `setup:${p.key}`,
    label: p.pillLabel,
    prompt: p.pillPrompt,
    minRole: p.minRole,
    scope: { kind: 'onboarding' },
  }))

/** Actions the user can perform on the screen they are already looking at. */
export const areaStarters = (area: NavArea | null, role: EnumUserRoles): AgentStarter[] => {
  if (!area) return []
  return ROUTE_CAPABILITIES[area].suggestions
    .filter((s) => roleAtLeast(role, s.minRole))
    .map((s) => ({ ...s, key: `page:${area}:${s.key}`, scope: { kind: 'area' as const, area } }))
}

/**
 * Curated cross-area jobs. `exclude` drops the area the user is already on, so
 * the command menu does not repeat what its page group already lists.
 */
export const discoveryStarters = (role: EnumUserRoles, exclude?: NavArea | null): AgentStarter[] =>
  DISCOVERY_ITEMS.filter((d) => roleAtLeast(role, d.minRole) && d.area !== exclude).map((d) => ({
    key: `quick:${d.key}`,
    label: d.label,
    prompt: d.prompt,
    minRole: d.minRole,
    scope: { kind: 'area', area: d.area },
  }))
